import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MdDelete, MdEdit, MdSearch, MdClear, MdPerson } from "react-icons/md";
import { 
  Box, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  IconButton, 
  TextField, 
  InputAdornment,
  Typography,
  Chip,
  Avatar,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Button
} from "@mui/material";
import { deleteBusinessStaff } from "../logic/business/action";

const StaffTable = ({ setEditMode, setAddStaffMode, onDeleteStaff, staffAlias = 'Staff' }) => {
  const dispatch = useDispatch();
  const selector = useSelector((state) => state.business);
  const selectorAuth = useSelector((state) => state.auth);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [roleFilter, setRoleFilter] = useState('all');

  const handleDeleteStaff = (staff_id) => {
    onDeleteStaff(staff_id);
  };

  const filteredStaff = useMemo(() => {
    if (!selector?.staff) return [];
    
    let filtered = selector.staff;

    // Apply search filter
    if (searchText) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(staff => 
        staff.name?.toLowerCase().includes(search) ||
        staff.email?.toLowerCase().includes(search) ||
        staff.contact?.toLowerCase().includes(search) ||
        staff.role?.toLowerCase().includes(search)
      );
    }

    // Apply role filter
    if (roleFilter !== 'all') {
      filtered = filtered.filter(staff => staff.role?.toLowerCase() === roleFilter.toLowerCase());
    }

    return filtered;
  }, [selector?.staff, searchText, roleFilter]);

  // Pagination
  const paginatedStaff = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredStaff.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredStaff, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClearFilters = () => {
    setSearchText('');
    setRoleFilter('all');
    setPage(0);
  };

  const hasActiveFilters = searchText || roleFilter !== 'all';

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name[0].toUpperCase();
  };

  const getColorFromName = (name) => {
    const colors = [
      '#5c6ac4', '#8b5cf6', '#ec4899', '#f59e0b', 
      '#10b981', '#3b82f6', '#ef4444', '#6366f1'
    ];
    if (!name) return colors[0];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        border: '1px solid #e5e7eb', 
        borderRadius: 2,
        overflow: 'hidden',
        backgroundColor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      }}
    >
      {/* Search and Filter Bar */}
      <Box sx={{ p: 2, borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
        <Stack 
          direction={{ xs: 'column', md: 'row' }} 
          spacing={2} 
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          {/* Search */}
          <TextField
            fullWidth
            size="small"
            placeholder={`Search ${staffAlias.toLowerCase()}s by name, email, phone, or role...`}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MdSearch style={{ color: '#6b7280' }} />
                </InputAdornment>
              ),
              endAdornment: searchText && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchText('');
                      setPage(0);
                    }}
                    sx={{ p: 0.5 }}
                  >
                    <MdClear size={18} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#ffffff',
                borderRadius: 1,
                '&:hover': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#d1d5db',
                  },
                },
                '&.Mui-focused': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#667eea',
                  },
                },
              },
            }}
          />

          {/* Role Filter */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={roleFilter}
              label="Role"
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setPage(0);
              }}
              sx={{
                borderRadius: 1,
                backgroundColor: '#ffffff',
              }}
            >
              <MenuItem value="all">All Roles</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="staff">Staff</MenuItem>
            </Select>
          </FormControl>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleClearFilters}
              startIcon={<MdClear />}
              sx={{
                textTransform: 'none',
                borderColor: '#d1d5db',
                color: '#6b7280',
                whiteSpace: 'nowrap',
                '&:hover': {
                  borderColor: '#9ca3af',
                  backgroundColor: '#f3f4f6',
                },
              }}
            >
              Clear
            </Button>
          )}
        </Stack>

        {/* Results Count */}
        <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Showing {paginatedStaff.length} of {filteredStaff.length} {staffAlias.toLowerCase()}{filteredStaff.length !== 1 ? 's' : ''}
            {hasActiveFilters && ' (filtered)'}
          </Typography>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, color: '#374151', backgroundColor: '#f9fafb' }}>
                {staffAlias}
              </TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#374151', backgroundColor: '#f9fafb' }}>
                Email
              </TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#374151', backgroundColor: '#f9fafb' }}>
                Phone Number
              </TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#374151', backgroundColor: '#f9fafb' }}>
                Role
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: '#374151', backgroundColor: '#f9fafb' }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedStaff.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <MdPerson size={48} style={{ color: '#9ca3af' }} />
                    <Typography variant="body2" color="text.secondary">
                      {hasActiveFilters 
                        ? `No ${staffAlias.toLowerCase()}s found matching your filters` 
                        : `No ${staffAlias.toLowerCase()}s added yet`}
                    </Typography>
                    {hasActiveFilters && (
                      <Button
                        variant="text"
                        size="small"
                        onClick={handleClearFilters}
                        sx={{ textTransform: 'none', mt: 1 }}
                      >
                        Clear all filters
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              paginatedStaff.map((staff) => (
                <TableRow 
                  key={staff.staff_id}
                  sx={{ 
                    '&:hover': { 
                      backgroundColor: '#f9fafb',
                      cursor: 'pointer'
                    },
                    transition: 'background-color 0.2s'
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar 
                        sx={{ 
                          bgcolor: getColorFromName(staff.name),
                          width: 40,
                          height: 40,
                          fontSize: '0.875rem',
                          fontWeight: 600
                        }}
                      >
                        {getInitials(staff.name)}
                      </Avatar>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1f2937' }}>
                        {staff.name || 'N/A'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {staff.email || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {staff.contact || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={staff.role || 'Staff'} 
                      size="small"
                      sx={{
                        backgroundColor: staff.role === 'admin' ? '#dbeafe' : '#f3f4f6',
                        color: staff.role === 'admin' ? '#1e40af' : '#374151',
                        fontWeight: 500,
                        fontSize: '0.75rem'
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => setAddStaffMode(staff)}
                        sx={{
                          color: '#5c6ac4',
                          '&:hover': {
                            backgroundColor: '#eef2ff',
                          },
                        }}
                      >
                        <MdEdit size={18} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteStaff(staff.staff_id)}
                        sx={{
                          color: '#ef4444',
                          '&:hover': {
                            backgroundColor: '#fee2e2',
                          },
                        }}
                      >
                        <MdDelete size={18} />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={filteredStaff.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        sx={{
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb',
          '& .MuiTablePagination-toolbar': {
            px: 2,
          },
          '& .MuiTablePagination-selectLabel': {
            fontSize: '0.875rem',
            color: '#6b7280',
          },
          '& .MuiTablePagination-displayedRows': {
            fontSize: '0.875rem',
            color: '#374151',
          },
          '& .MuiTablePagination-actions': {
            '& .MuiIconButton-root': {
              color: '#667eea',
              '&:hover': {
                backgroundColor: 'rgba(102, 126, 234, 0.1)',
              },
              '&.Mui-disabled': {
                color: '#d1d5db',
              },
            },
          },
        }}
      />
    </Paper>
  );
};

export const StaffTableMobile = ({ setEditMode, setAddStaffMode, onDeleteStaff, staffAlias = 'Staff' }) => {
  const dispatch = useDispatch();
  const selector = useSelector((state) => state.business);
  const selectorAuth = useSelector((state) => state.auth);
  const [searchText, setSearchText] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleDeleteStaff = (staff_id) => {
    onDeleteStaff(staff_id);
  };

  const filteredStaff = useMemo(() => {
    if (!selector?.staff) return [];
    
    let filtered = selector.staff;

    // Apply search filter
    if (searchText) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(staff => 
        staff.name?.toLowerCase().includes(search) ||
        staff.email?.toLowerCase().includes(search) ||
        staff.contact?.toLowerCase().includes(search) ||
        staff.role?.toLowerCase().includes(search)
      );
    }

    // Apply role filter
    if (roleFilter !== 'all') {
      filtered = filtered.filter(staff => staff.role?.toLowerCase() === roleFilter.toLowerCase());
    }

    return filtered;
  }, [selector?.staff, searchText, roleFilter]);

  // Pagination
  const paginatedStaff = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredStaff.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredStaff, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClearFilters = () => {
    setSearchText('');
    setRoleFilter('all');
    setPage(0);
  };

  const hasActiveFilters = searchText || roleFilter !== 'all';

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name[0].toUpperCase();
  };

  const getColorFromName = (name) => {
    const colors = [
      '#5c6ac4', '#8b5cf6', '#ec4899', '#f59e0b', 
      '#10b981', '#3b82f6', '#ef4444', '#6366f1'
    ];
    if (!name) return colors[0];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        border: '1px solid #e5e7eb', 
        borderRadius: 2,
        overflow: 'hidden',
        backgroundColor: '#ffffff'
      }}
    >
      {/* Search and Filter Bar */}
      <Box sx={{ p: 2, borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
        <Stack direction="column" spacing={2}>
          <TextField
            fullWidth
            size="small"
            placeholder={`Search ${staffAlias.toLowerCase()}s...`}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MdSearch style={{ color: '#6b7280' }} />
                </InputAdornment>
              ),
              endAdornment: searchText && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchText('');
                      setPage(0);
                    }}
                    sx={{ p: 0.5 }}
                  >
                    <MdClear size={18} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#ffffff',
                borderRadius: 1,
              },
            }}
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={roleFilter}
                label="Role"
                onChange={(e) => {
                  setRoleFilter(e.target.value);
                  setPage(0);
                }}
                sx={{
                  borderRadius: 1,
                  backgroundColor: '#ffffff',
                }}
              >
                <MenuItem value="all">All Roles</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="staff">Staff</MenuItem>
              </Select>
            </FormControl>
            {hasActiveFilters && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleClearFilters}
                startIcon={<MdClear />}
                sx={{
                  textTransform: 'none',
                  borderColor: '#d1d5db',
                  color: '#6b7280',
                  '&:hover': {
                    borderColor: '#9ca3af',
                    backgroundColor: '#f3f4f6',
                  },
                }}
              >
                Clear
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>

      {/* Mobile Card List */}
      <Box sx={{ p: 2 }}>
        {paginatedStaff.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <MdPerson size={48} style={{ color: '#9ca3af' }} />
              <Typography variant="body2" color="text.secondary">
                {hasActiveFilters 
                  ? `No ${staffAlias.toLowerCase()}s found matching your filters` 
                  : `No ${staffAlias.toLowerCase()}s added yet`}
              </Typography>
              {hasActiveFilters && (
                <Button
                  variant="text"
                  size="small"
                  onClick={handleClearFilters}
                  sx={{ textTransform: 'none', mt: 1 }}
                >
                  Clear all filters
                </Button>
              )}
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {paginatedStaff.map((staff) => (
              <Paper
                key={staff.staff_id}
                elevation={0}
                sx={{
                  p: 2,
                  border: '1px solid #e5e7eb',
                  borderRadius: 1.5,
                  '&:hover': {
                    borderColor: '#d1d5db',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  },
                  transition: 'all 0.2s'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: getColorFromName(staff.name),
                        width: 48,
                        height: 48,
                        fontSize: '1rem',
                        fontWeight: 600
                      }}
                    >
                      {getInitials(staff.name)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1f2937' }}>
                        {staff.name || 'N/A'}
                      </Typography>
                      <Chip 
                        label={staff.role || 'Staff'} 
                        size="small"
                        sx={{
                          mt: 0.5,
                          backgroundColor: staff.role === 'admin' ? '#dbeafe' : '#f3f4f6',
                          color: staff.role === 'admin' ? '#1e40af' : '#374151',
                          fontWeight: 500,
                          fontSize: '0.7rem',
                          height: 20
                        }}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => setAddStaffMode(staff)}
                      sx={{
                        color: '#5c6ac4',
                        '&:hover': { backgroundColor: '#eef2ff' },
                      }}
                    >
                      <MdEdit size={18} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteStaff(staff.staff_id)}
                      sx={{
                        color: '#ef4444',
                        '&:hover': { backgroundColor: '#fee2e2' },
                      }}
                    >
                      <MdDelete size={18} />
                    </IconButton>
                  </Box>
                </Box>
                {staff.email && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    📧 {staff.email}
                  </Typography>
                )}
                {staff.contact && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    📱 {staff.contact}
                  </Typography>
                )}
              </Paper>
            ))}
          </Box>
        )}
      </Box>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={filteredStaff.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb',
          '& .MuiTablePagination-toolbar': {
            px: 2,
            flexWrap: 'wrap',
          },
          '& .MuiTablePagination-selectLabel': {
            fontSize: '0.875rem',
            color: '#6b7280',
          },
          '& .MuiTablePagination-displayedRows': {
            fontSize: '0.875rem',
            color: '#374151',
          },
          '& .MuiTablePagination-actions': {
            '& .MuiIconButton-root': {
              color: '#667eea',
              '&:hover': {
                backgroundColor: 'rgba(102, 126, 234, 0.1)',
              },
              '&.Mui-disabled': {
                color: '#d1d5db',
              },
            },
          },
        }}
      />
    </Paper>
  );
};





export default StaffTable;
